import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { catchError, map, mergeMap, of, switchMap, take, withLatestFrom } from 'rxjs';
import {
    CalculateLicenseEndDate,
    CalculateLicenseEndDateFailure,
    CalculateLicenseEndDateSuccess,
    CancelEmployeeLeaving,
    CancelEmployeeLeavingFailure,
    CancelEmployeeLeavingSuccess,
    LoadAllEmployeeLeavingRequestsByCompany,
    LoadAllEmployeeLeavingRequestsByCompanyFailure,
    LoadAllEmployeeLeavingRequestsByCompanySuccess,
    ScheduleEmployeeLeaving,
    ScheduleEmployeeLeavingFailure,
    ScheduleEmployeeLeavingSuccess,
    UpdateEmployeeLeavingDate,
    UpdateEmployeeLeavingDateFailure,
    UpdateEmployeeLeavingDateSuccess
} from '../actions/employee-leaving.actions';
import { EmployeeLeavingRequestManagementControllerService } from '@iota/inh-core-v1-management-client';
import { CompanyEmployeeOverviewPageParams, LoadEmployeeFilterCounts } from '../actions/company-employee.actions';
import { companyEmployeeLastOverviewPageParams } from '../reducer/company-employee.reducer';
import { selectedCompanyId } from '../store';
import { toOverviewPageReload } from './employee-overview-reload';

@Injectable()
export class EmployeeLeavingEffects {
    loadAllEmployeeLeavingRequestsByCompany$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadAllEmployeeLeavingRequestsByCompany),
            mergeMap(({ companyId }) =>
                this.api.getAllByCompany(companyId).pipe(
                    take(1),
                    map((employeeLeavingRequests) => LoadAllEmployeeLeavingRequestsByCompanySuccess({ employeeLeavingRequests })),
                    catchError((error) => of(LoadAllEmployeeLeavingRequestsByCompanyFailure({ error })))
                )
            )
        )
    );

    scheduleEmployeeLeaving$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ScheduleEmployeeLeaving),
            withLatestFrom(this.store.select(selectedCompanyId), this.store.select(companyEmployeeLastOverviewPageParams)),
            mergeMap(([{ request }, companyId, lastParams]) =>
                this.api.scheduleLeaving(request).pipe(
                    take(1),
                    switchMap((employeeLeavingRequest) => [
                        ScheduleEmployeeLeavingSuccess({ employeeLeavingRequest }),
                        ...this.toOverviewReloadActions(companyId, lastParams)
                    ]),
                    catchError((error) => of(ScheduleEmployeeLeavingFailure({ error })))
                )
            )
        )
    );

    updateEmployeeLeavingDate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpdateEmployeeLeavingDate),
            withLatestFrom(this.store.select(selectedCompanyId), this.store.select(companyEmployeeLastOverviewPageParams)),
            mergeMap(([{ id, request }, companyId, lastParams]) =>
                this.api.updateCompanyLeavingDate(id, request).pipe(
                    take(1),
                    switchMap((employeeLeavingRequest) => [
                        UpdateEmployeeLeavingDateSuccess({ employeeLeavingRequest }),
                        ...this.toOverviewReloadActions(companyId, lastParams)
                    ]),
                    catchError((error) => of(UpdateEmployeeLeavingDateFailure({ error })))
                )
            )
        )
    );

    cancelEmployeeLeaving$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CancelEmployeeLeaving),
            withLatestFrom(this.store.select(selectedCompanyId), this.store.select(companyEmployeeLastOverviewPageParams)),
            mergeMap(([{ id }, companyId, lastParams]) =>
                this.api.cancelEmployeeLeaving(id).pipe(
                    take(1),
                    switchMap((employeeLeavingRequest) => [
                        CancelEmployeeLeavingSuccess({ employeeLeavingRequest }),
                        ...this.toOverviewReloadActions(companyId, lastParams)
                    ]),
                    catchError((error) => of(CancelEmployeeLeavingFailure({ error })))
                )
            )
        )
    );

    calculateLicenseEndDate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CalculateLicenseEndDate),
            mergeMap(({ companyLeavingDate }) =>
                this.api.getCalculatedLicenseEndDate(companyLeavingDate).pipe(
                    take(1),
                    map((licenseEndDate) => CalculateLicenseEndDateSuccess({ licenseEndDate })),
                    catchError((error) => of(CalculateLicenseEndDateFailure({ error })))
                )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private api: EmployeeLeavingRequestManagementControllerService,
        private store: Store
    ) {}

    private toOverviewReloadActions(companyId: string, lastParams: CompanyEmployeeOverviewPageParams | null): Action[] {
        return [LoadEmployeeFilterCounts({ companyId }), ...toOverviewPageReload(companyId, lastParams)];
    }
}
